import {
  ActionGroup,
  Button,
  Form,
  FormGroup,
  TextInput,
  LoginMainFooterBandItem,
  LoginPage,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router-dom'

import bgImgSrcSm from '@app/assets/images/intake-login-bg-sm.png'
import bgImgSrcXSm from '@app/assets/images/intake-login-bg-xsm.png'
import bgImgSrc from '@app/assets/images/intake-login-bg.png'
import logoImgSrc from '@app/assets/images/intake-logo-landing.png'
import * as Auth from '@app/state/authenticate'

enum EmailValidationStatusId {
  Default = 'default',
  Error = 'error',
  Success = 'success',
}

export const ForgotPasswordScreen = () => {
  const dispatch = useDispatch()

  const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [emailValidated, setEmailValidated] = useState<EmailValidationStatusId>(
    EmailValidationStatusId.Default,
  )
  const [isFormComplete, setIsFormComplete] = useState<boolean>(true)

  useEffect(() => {
    const isComplete = email != null && email.trim() !== ''
    setIsFormComplete(isComplete)
  }, [email])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setRequestSubmitted(true)
      dispatch(Auth.actions.forgotPassword(email))
    },
    [dispatch, email],
  )

  const handleEmailUpdate = useCallback((newEmail: string) => {
    const isEmailValid = newEmail != null && newEmail.trim() !== ''
    setEmail(newEmail)
    setEmailValidated(
      isEmailValid
        ? EmailValidationStatusId.Success
        : EmailValidationStatusId.Error,
    )
  }, [])

  const forgotPasswordForm = (
    <Form onSubmit={handleSubmit}>
      <FormGroup
        label="Email Address"
        isRequired
        fieldId="email-address"
        helperText="Please provide your email address"
      >
        <TextInput
          value={email}
          isRequired
          type="text"
          aria-desribedby="email-address"
          id="email-address"
          name="email-address"
          validated={emailValidated}
          onChange={handleEmailUpdate}
        />
      </FormGroup>
      <ActionGroup>
        <Button
          type="submit"
          isBlock
          variant="primary"
          isDisabled={!isFormComplete}
        >
          Reset Password
        </Button>
      </ActionGroup>
    </Form>
  )

  const returnToLoginPage = (
    <LoginMainFooterBandItem>
      <NavLink exact to={'/'} activeClassName="pf-m-current">
        Remember your password? Sign in here
      </NavLink>
    </LoginMainFooterBandItem>
  )

  const images = {
    lg: bgImgSrc,
    sm: bgImgSrcSm,
    sm2x: bgImgSrcSm,
    xs: bgImgSrcXSm,
    xs2x: bgImgSrcXSm,
  }

  const loginSubtitle = requestSubmitted
    ? 'If an account exists, details to recover your account will be sent to the provided email address.'
    : 'Please enter your email address. An email will be sent to you with a secure link to reset your password.'

  return (
    <LoginPage
      brandImgSrc={logoImgSrc}
      loginTitle="Forgot your password?"
      loginSubtitle={loginSubtitle}
      forgotCredentials={returnToLoginPage}
      backgroundImgSrc={images}
    >
      {requestSubmitted ? null : forgotPasswordForm}
    </LoginPage>
  )
}
